import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { CheckCircle2, AlertTriangle, XCircle, RefreshCw, Send } from 'lucide-react';
import { SaButton, errMsg } from './ui';
import { LeadAlertCard } from './LeadAlertCard';

const TONE = { pass: ['#22c55e', CheckCircle2], warn: ['#fbbf24', AlertTriangle], fail: ['#ef4444', XCircle] };

const Check = ({ c }) => {
  const [color, Icon] = TONE[c.status] || TONE.warn;
  return (
    <div data-testid={`lr-check-${c.id}`} style={{ display: 'grid', gridTemplateColumns: '22px 1fr', gap: 12, padding: '12px 0', borderBottom: '1px solid #1F1F23' }}>
      <Icon size={18} color={color} style={{ marginTop: 2 }} />
      <div>
        <div style={{ fontWeight: 700 }}>{c.label} <span data-testid={`lr-status-${c.id}`} style={{ color, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.12em', marginLeft: 8 }}>{c.status}</span></div>
        <div style={{ fontSize: 13, color: '#A1A1AA', marginTop: 2 }}>{c.detail}</div>
        {c.action && <div style={{ fontSize: 12, color: '#fbbf24', marginTop: 4 }}>→ {c.action}</div>}
      </div>
    </div>
  );
};

const EmailTestCard = () => {
  const [status, setStatus] = useState(null);
  const [to, setTo] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  useEffect(() => { axios.get('/superadmin/platform-email/status').then((r) => setStatus(r.data)).catch(() => {}); }, []);
  const send = async () => {
    setBusy(true); setResult(null);
    try { const r = await axios.post('/superadmin/platform-email/test', { to }); setResult(r.data); toast.success(r.data.sent ? 'Test email processed' : 'Send failed'); }
    catch (e) { toast.error(errMsg(e, 'Could not send')); } finally { setBusy(false); }
  };
  if (!status) return null;
  return (
    <div data-testid="lr-email-card" style={{ background: '#141414', border: '1px solid #27272A', padding: 18, marginBottom: 18 }}>
      <div style={{ fontWeight: 800, marginBottom: 6 }}>Platform email (DecoWorkflow sender)</div>
      <div style={{ fontSize: 13, color: '#A1A1AA' }}>
        Mode: <b data-testid="lr-email-mode" style={{ color: status.mode === 'smtp' ? '#22c55e' : '#fbbf24' }}>{status.mode.toUpperCase()}</b>
        {' · '}SMTP {status.configured ? `configured (${status.host}:${status.port})` : 'not configured'} · From: {status.from_email || '—'}
        {status.mode === 'log' && <span> · emails are stored in the outbox, nothing leaves the system</span>}
      </div>
      <div style={{ display: 'flex', gap: 8, marginTop: 12, alignItems: 'center' }}>
        <input data-testid="lr-email-to" type="email" placeholder="you@example.com" value={to} onChange={(e) => setTo(e.target.value)}
          style={{ background: '#0A0A0A', color: '#fff', border: '1px solid #3F3F46', padding: '8px 10px', fontSize: 13, width: 280 }} />
        <SaButton data-testid="lr-email-send" disabled={busy || !to} onClick={send}><Send size={12} /> Send test email</SaButton>
      </div>
      {result && <div data-testid="lr-email-result" style={{ fontSize: 12, marginTop: 10, color: result.sent ? '#22c55e' : '#ef4444' }}>{result.note}</div>}
    </div>
  );
};

export const LaunchReadiness = () => {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => { setBusy(true); try { setData((await axios.get('/superadmin/launch-readiness')).data); } catch (e) { toast.error(errMsg(e, 'Could not load readiness')); } finally { setBusy(false); } }, []);
  useEffect(() => { load(); }, [load]);
  if (!data) return <div style={{ color: '#A1A1AA', padding: 20 }}>Running checks…</div>;
  return (
    <div data-testid="sa-launch-readiness">
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 18, flexWrap: 'wrap' }}>
        <div data-testid="lr-overall" style={{ fontSize: 22, fontWeight: 800, color: data.ready ? '#22c55e' : '#ef4444' }}>{data.ready ? 'No blocking failures' : `${data.totals.fail} blocking issue${data.totals.fail === 1 ? '' : 's'}`}</div>
        <div style={{ fontSize: 12, color: '#A1A1AA' }}>environment: <b data-testid="lr-environment" style={{ color: '#fff' }}>{data.environment}</b> · {data.platform.app_host} / {data.platform.admin_host}</div>
        <div style={{ display: 'flex', gap: 10, fontSize: 12 }}>
          {['pass', 'warn', 'fail'].map((k) => <span key={k} data-testid={`lr-total-${k}`} style={{ color: TONE[k][0], fontWeight: 700 }}>{data.totals[k]} {k}</span>)}
        </div>
        <SaButton variant="ghost" data-testid="lr-refresh" disabled={busy} onClick={load}><RefreshCw size={12} /> Re-run</SaButton>
      </div>
      <EmailTestCard />
      <LeadAlertCard />
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(420px, 1fr))', gap: 18 }}>
        {data.sections.map((s) => (
          <div key={s.id} data-testid={`lr-section-${s.id}`} style={{ background: '#141414', border: '1px solid #27272A', padding: '6px 18px 10px' }}>
            <div style={{ fontSize: 11, color: '#A1A1AA', textTransform: 'uppercase', letterSpacing: '0.14em', padding: '12px 0 4px' }}>{s.title}</div>
            {s.checks.map((c) => <Check key={c.id} c={c} />)}
          </div>
        ))}
      </div>
      <p style={{ fontSize: 12, color: '#71717A', marginTop: 16 }}>Generated {new Date(data.generated_at).toLocaleString()} · secrets are never displayed, only whether they are set. Full runbook: /app/memory/LAUNCH_CHECKLIST.md</p>
    </div>
  );
};
